import { Component, ElementRef, OnInit, ViewChild, Renderer2 } from '@angular/core';
import { Controller } from '../../../coverpath-client/public/js/Controller';
import { AmorphicService } from '../../angular/services/amorphic/amorphic.service';
import { getDocument } from 'pdfjs-dist/build/pdf';
import { BaseModalComponent } from '../../angular/components/modals/base-modal.component';
import { ModalService } from '../../angular/services/modal/modal.service';
import { UICategory, UILoggerSingleton } from '../../../common/js/logger/UILoggerSingleton';

interface FormToSign {
  formType: string;
  pdfUrl: string;
  textPrompt: string;
  eSignLabel: string;
  signFn(): void;
}

@Component({
  selector: 'esign-modal',
  moduleId: module.id,
  templateUrl: './esign-modal.component.html',
})
export class EsignModalComponent extends BaseModalComponent implements OnInit {
  onFinishSigning: any;
  cancel() {}
  confirm() {}
  @ViewChild('pdfCanvasContainer') pdfCanvasContainer: ElementRef;
  data;
  controller: Controller;
  formsToSign: Array<FormToSign> = [];
  loadingScreen: boolean = false;
  esignDisabled = true;
  pdfRendered = false;
  errorRenderingPdf = false;
  errorSigningPdf = false;
  docIndex: number = 0;
  hideEsignButton?: boolean = false;
  isViewPolicy?: boolean = false;

  constructor(
    protected modalService: ModalService,
    private renderer: Renderer2,
    private amorphicService: AmorphicService
  ) {
    super(modalService);
    this.controller = this.amorphicService.controller as Controller;
  }

  async ngOnInit() {
    this.formsToSign = this.data.formsToSign;
    this.onFinishSigning = this.data.onFinishSigning;
    this.hideEsignButton = this.data.hideEsignButton ? this.data.hideEsignButton : false;
    this.isViewPolicy = this.data.isViewPolicy ? this.data.isViewPolicy : false;
    if (this.formsToSign.length === 0) {
      UILoggerSingleton.getInstance().error(
        {
          module: 'EsignModalComponent',
          function: 'ngOnInit',
          category: UICategory.request,
        },
        'Do not have forms to e-sign'
      );

      this.closeModal();
    } else {
      this.docIndex = 0;
      this.showPdf();
    }
  }

  closeModal(): void {
    this.modalService.deactivate();
  }

  download() {
    window.open(this.controller.getApplicationPdfURL(true), '_self');
  }

  async showPdf() {
    const formToSign = this.formsToSign[this.docIndex];

    try {
      this.loadingScreen = true;
      const clientPdf = await getDocument(formToSign.pdfUrl);
      await this.renderPages(clientPdf);
      this.pdfRendered = true;
      this.esignDisabled = false;
    } catch (err) {
      const error = {
        isHumanRelated: false,
        data: {
          formType: formToSign.formType,
        },
        name: err.name,
        message: err.message,
        stack: err.stack,
      };
      UILoggerSingleton.getInstance().error({
        module: 'EsignModalComponent',
        function: 'showPdf',
        category: UICategory.request,
        error: error,
      });

      this.errorRenderingPdf = true;
    } finally {
      this.loadingScreen = false;
    }
  }

  async renderPages(pdfDoc) {
    //first remove any existing children elements to the container (which would be present from a previous pdf)
    this.pdfCanvasContainer.nativeElement.innerHTML = '';
    for (let num = 1; num <= pdfDoc.numPages; num++) {
      const currentPage = await pdfDoc.getPage(num);
      const scale = 1.25;
      const viewport = currentPage.getViewport(scale);
      const child = this.renderer.createElement('canvas');
      this.renderer.addClass(child, 'client-esign__pdf-canvas');
      this.renderer.setProperty(child, 'height', viewport.height);
      this.renderer.setProperty(child, 'width', viewport.width);
      this.pdfCanvasContainer.nativeElement.appendChild(child);
      const context = (child as HTMLCanvasElement).getContext('2d');
      const renderContext = {
        canvasContext: context,
        viewport,
      };

      await currentPage.render(renderContext);
    }
  }

  async signForm() {
    try {
      this.loadingScreen = true;
      this.esignDisabled = true;
      UILoggerSingleton.getInstance().info(
        {
          component: 'browser',
          module: 'EsignModalComponent',
          function: 'signForm',
          category: UICategory.request,
          data: {
            formType: this.formsToSign[this.docIndex].formType,
          },
        },
        'singing Form'
      );
      await Promise.resolve(this.formsToSign[this.docIndex].signFn());

      this.docIndex++;

      //show the next form if there is one, or accept the policy if thr last form was just signed
      if (this.docIndex < this.formsToSign.length) {
        await this.showPdf();
      } else {
        await this.onFinishSigning();
        this.closeModal();
      }
    } catch (err) {
      const error = {
        isHumanRelated: false,
        data: {
          formType: this.formsToSign[this.docIndex].formType,
        },
        name: err.name,
        message: err.message,
        stack: err.stack,
      };
      UILoggerSingleton.getInstance().error({
        module: 'EsignModalComponent',
        function: 'signForm',
        category: UICategory.request,
        error: error,
      });

      this.errorSigningPdf = true;
    } finally {
      this.loadingScreen = false;
    }
  }
}
